'use client';

import { Box } from '@chakra-ui/react';
import { useEffect, useRef, type CSSProperties } from 'react';

import {
  connectCanvasPreviewBridge,
  type CanvasPreviewMessageFromFrame,
  type CanvasPreviewMessageToFrame,
} from './canvas-preview-bridge';

export function CanvasPreviewFrame({
  src,
  live,
  title,
  frameStyle,
  selectionMode,
  onMessage,
  onSenderChange,
  onUnavailable,
  onReadyChange,
  onFrameReset,
  interactive = true,
}: {
  src: string;
  live: boolean;
  title: string;
  frameStyle: CSSProperties;
  selectionMode: boolean;
  onMessage: (message: CanvasPreviewMessageFromFrame) => void;
  onSenderChange: (sender: ((message: CanvasPreviewMessageToFrame) => void) | null) => void;
  onUnavailable: () => void;
  onReadyChange?: (ready: boolean) => void;
  onFrameReset?: () => void;
  interactive?: boolean;
}) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const senderRef = useRef<((message: CanvasPreviewMessageToFrame) => void) | null>(null);
  const onMessageRef = useRef(onMessage);
  const onSenderChangeRef = useRef(onSenderChange);
  const onUnavailableRef = useRef(onUnavailable);
  const onReadyChangeRef = useRef(onReadyChange);
  const onFrameResetRef = useRef(onFrameReset);
  const frameStyleRef = useRef(frameStyle);
  const selectionModeRef = useRef(selectionMode);
  onMessageRef.current = onMessage;
  onSenderChangeRef.current = onSenderChange;
  onUnavailableRef.current = onUnavailable;
  onReadyChangeRef.current = onReadyChange;
  onFrameResetRef.current = onFrameReset;
  frameStyleRef.current = frameStyle;
  selectionModeRef.current = selectionMode;

  useEffect(() => {
    if (iframeRef.current) Object.assign(iframeRef.current.style, frameStyle);
  }, [frameStyle]);

  useEffect(() => {
    if (iframeRef.current) iframeRef.current.title = title;
  }, [title]);

  useEffect(() => {
    senderRef.current?.({ source: 'attune-host', type: 'selection-mode', enabled: selectionMode });
  }, [selectionMode]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    container.replaceChildren();
    const iframe = document.createElement('iframe');
    iframe.title = title;
    iframe.src = src;
    iframe.referrerPolicy = 'no-referrer';
    iframe.allow = '';
    iframe.setAttribute('sandbox', live ? 'allow-same-origin allow-scripts' : 'allow-scripts');
    Object.assign(iframe.style, frameStyleRef.current);
    iframeRef.current = iframe;
    const bridge = connectCanvasPreviewBridge({
      iframe,
      hostWindow: window,
      live,
      selectionMode: () => selectionModeRef.current,
      onMessage: message => onMessageRef.current(message),
      onReadyChange: ready => onReadyChangeRef.current?.(ready),
      onUnavailable: () => onUnavailableRef.current(),
      onFrameReset: () => onFrameResetRef.current?.(),
    });
    senderRef.current = bridge.send;
    onSenderChangeRef.current(bridge.send);
    container.append(iframe);

    return () => {
      bridge.dispose();
      onReadyChangeRef.current?.(false);
      iframeRef.current = null;
      senderRef.current = null;
      onSenderChangeRef.current(null);
      container.replaceChildren();
    };
  }, [live, src]);

  return (
    <Box
      ref={containerRef}
      position="absolute"
      inset="0"
      pointerEvents={interactive ? 'auto' : 'none'}
    />
  );
}
